import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Pause, Play, X, Ticket, Instagram, Share2, Facebook, Link2, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { trackEvent } from '../lib/analytics';
import './UpcomingEvents.css';

const EVENTBRITE_URL = 'https://www.eventbrite.com/o/royal-bar-grill-120928056108';
const KARAOKE_IG_URL = 'https://www.instagram.com/giveitupforbryson/';
const PUB = process.env.PUBLIC_URL || '';

// hideNum = YYYYMMDD in America/Los_Angeles; the slide disappears once LA date >= hideNum.
const EVENTS = [
  {
    id: 'karaoke-idol',
    poster: `${PUB}/posters/karaoke-idol.webp`,
    title: "Daniel's Karaoke Idol",
    hideNum: 20261115,
    highlight: 'Free to enter • $300+ in prizes • Participants 21+',
    schedule: [
      'Oct 10 — 9 PM: Auditions, Round 1',
      'Oct 17 — 10 PM: Auditions, Round 2',
      'Nov 7 — 9 PM: Themes, Round 3',
      'Nov 14 — 9 PM: Finale, Round 4',
    ],
    note: 'Participants must be available for one audition date and both November 7 and November 14.',
    button: { label: 'Sign Up & Ask Questions', href: KARAOKE_IG_URL, Icon: Instagram, event: 'karaoke_signup_click' },
  },
  {
    id: 'comedy-showcase-8',
    poster: `${PUB}/posters/comedy-showcase-8.webp`,
    title: 'Comedy Showcase #8',
    hideNum: 20261018,
    highlight: 'Saturday, October 17, 2026 • 7 PM',
    schedule: [
      '$5 cover • Limited seating',
      'Headliner: Armando Santoyo',
      'Co-headliner: Angie Bloomfield',
      'Host: Terry Geil',
      'Opener: Jamie Colson',
    ],
    note: null,
    button: { label: 'Reserve Your Seat', href: EVENTBRITE_URL, Icon: Ticket, event: 'reserve_seat_click' },
  },
  {
    id: 'royal-unplugged',
    poster: `${PUB}/posters/royal-unplugged.webp`,
    title: 'Royal Unplugged — featuring Unknown Connection',
    hideNum: 20261025,
    highlight: 'Saturday, October 24, 2026 • 7–9 PM',
    schedule: [
      'Acoustic duo • Rock, alternative & country',
      'No cover',
    ],
    note: null,
    button: { label: 'Reserve a Table', href: EVENTBRITE_URL, Icon: Ticket, event: 'reserve_table_click' },
  },
];

const getLATodayNum = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const get = (t) => parts.find((p) => p.type === t).value;
  return parseInt(`${get('year')}${get('month')}${get('day')}`, 10);
};

const UpcomingEvents = () => {
  const todayNum = getLATodayNum();
  const events = EVENTS.filter((e) => todayNum < e.hideNum);

  const [index, setIndex] = useState(0);
  const [manualPaused, setManualPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [shareFor, setShareFor] = useState(null);
  const touchStartX = useRef(null);

  const count = events.length;
  const isSingle = count === 1;
  const paused = manualPaused || hovered || focused || !!lightbox;

  const goTo = useCallback((i) => setIndex((i + count) % count), [count]);
  const next = useCallback(() => setIndex((i) => (i + 1) % count), [count]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + count) % count), [count]);

  useEffect(() => {
    if (isSingle || paused) return undefined;
    const t = setInterval(() => setIndex((i) => (i + 1) % count), 3000);
    return () => clearInterval(t);
  }, [isSingle, paused, count]);

  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  if (count === 0) return null;

  const shareUrl = `${(typeof window !== 'undefined' ? window.location.origin : 'https://royalbaroregon.com')}/#upcoming-events`;
  const shareText = (ev) => `${ev.title} at Royal Bar & Grill${ev.highlight ? ` — ${ev.highlight}` : ''}`;

  const doShare = (ev, network) => {
    const url = encodeURIComponent(shareUrl);
    const text = encodeURIComponent(shareText(ev));
    trackEvent('event_share', { network, event_name: ev.id });
    if (network === 'copy') {
      navigator.clipboard?.writeText(shareUrl)
        .then(() => toast.success('Link copied — paste it anywhere to share!'))
        .catch(() => toast.error('Could not copy the link'));
      setShareFor(null);
      return;
    }
    const links = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      x: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      whatsapp: `https://wa.me/?text=${text}%20${url}`,
    };
    window.open(links[network], '_blank', 'noopener,noreferrer');
    setShareFor(null);
  };

  const nativeOrToggle = (ev) => {
    if (navigator.share) {
      navigator.share({ title: ev.title, text: shareText(ev), url: shareUrl }).catch(() => {});
      trackEvent('event_share', { network: 'native', event_name: ev.id });
      return;
    }
    setShareFor((cur) => (cur === ev.id ? null : ev.id));
  };

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null || isSingle) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (delta > 45) prev();
    else if (delta < -45) next();
    touchStartX.current = null;
  };

  return (
    <section id="upcoming-events" className="ue-section" data-testid="upcoming-events-section">
      <div className="ue-inner">
        <div className="ue-header">
          <h2 className="ue-title">Upcoming at Royal</h2>
          {!isSingle && (
            <button
              type="button"
              className="ue-playpause"
              onClick={() => setManualPaused((p) => !p)}
              aria-label={manualPaused ? 'Play slideshow' : 'Pause slideshow'}
              data-testid="ue-playpause"
            >
              {manualPaused ? <Play size={16} /> : <Pause size={16} />}
              <span>{manualPaused ? 'Play' : 'Pause'}</span>
            </button>
          )}
        </div>

        <div
          className="ue-carousel"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onFocusCapture={() => setFocused(true)}
          onBlurCapture={() => setFocused(false)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          role="region"
          aria-roledescription="carousel"
          aria-label="Upcoming events at Royal Bar & Grill"
          data-testid="ue-carousel"
        >
          {events.map((ev, i) => {
            const Btn = ev.button.Icon;
            return (
              <article
                key={ev.id}
                className={`ue-slide ${i === index ? 'active' : ''}`}
                aria-hidden={i !== index}
                data-testid={`ue-slide-${ev.id}`}
              >
                <button
                  type="button"
                  className="ue-poster-btn"
                  onClick={() => setLightbox(ev)}
                  aria-label={`Enlarge ${ev.title} poster`}
                  tabIndex={i === index ? 0 : -1}
                  data-testid={`ue-poster-${ev.id}`}
                >
                  <img src={ev.poster} alt={`${ev.title} poster`} className="ue-poster-img" loading="lazy" />
                  <span className="ue-poster-hint">Tap to enlarge</span>
                </button>

                <div className="ue-details">
                  <h3 className="ue-event-title">{ev.title}</h3>
                  {ev.highlight && <p className="ue-highlight">{ev.highlight}</p>}
                  <ul className="ue-schedule">
                    {ev.schedule.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                  {ev.note && <p className="ue-note">{ev.note}</p>}
                  <div className="ue-actions">
                    <a
                      href={ev.button.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ue-btn"
                      tabIndex={i === index ? 0 : -1}
                      onClick={() => trackEvent(ev.button.event, { link_location: 'upcoming_slideshow', event_name: ev.id })}
                      data-testid={`ue-cta-${ev.id}`}
                    >
                      <Btn size={18} />
                      {ev.button.label}
                    </a>
                    <button
                      type="button"
                      className="ue-share-btn"
                      onClick={() => nativeOrToggle(ev)}
                      tabIndex={i === index ? 0 : -1}
                      aria-label={`Share ${ev.title}`}
                      aria-expanded={shareFor === ev.id}
                      data-testid={`ue-share-${ev.id}`}
                    >
                      <Share2 size={16} />
                      Share
                    </button>
                  </div>

                  {shareFor === ev.id && (
                    <div className="ue-share-row" data-testid={`ue-share-row-${ev.id}`}>
                      <button type="button" className="ue-share-opt facebook" onClick={() => doShare(ev, 'facebook')} aria-label="Share on Facebook" data-testid={`ue-share-facebook-${ev.id}`}>
                        <Facebook size={18} />
                      </button>
                      <button type="button" className="ue-share-opt x" onClick={() => doShare(ev, 'x')} aria-label="Share on X" data-testid={`ue-share-x-${ev.id}`}>
                        <X size={18} />
                      </button>
                      <button type="button" className="ue-share-opt whatsapp" onClick={() => doShare(ev, 'whatsapp')} aria-label="Share on WhatsApp" data-testid={`ue-share-whatsapp-${ev.id}`}>
                        <MessageCircle size={18} />
                      </button>
                      <button type="button" className="ue-share-opt copy" onClick={() => doShare(ev, 'copy')} aria-label="Copy link" data-testid={`ue-share-copy-${ev.id}`}>
                        <Link2 size={18} />
                      </button>
                    </div>
                  )}
                </div>
              </article>
            );
          })}

          {!isSingle && (
            <>
              <button type="button" className="ue-arrow ue-arrow-prev" onClick={prev} aria-label="Previous event" data-testid="ue-prev">
                <ChevronLeft size={26} />
              </button>
              <button type="button" className="ue-arrow ue-arrow-next" onClick={next} aria-label="Next event" data-testid="ue-next">
                <ChevronRight size={26} />
              </button>
            </>
          )}
        </div>

        {!isSingle && (
          <div className="ue-dots" role="tablist" aria-label="Select event" data-testid="ue-dots">
            {events.map((ev, i) => (
              <button
                key={ev.id}
                type="button"
                className={`ue-dot ${i === index ? 'active' : ''}`}
                onClick={() => goTo(i)}
                aria-label={`Go to ${ev.title}`}
                aria-selected={i === index}
                role="tab"
                data-testid={`ue-dot-${i}`}
              />
            ))}
          </div>
        )}
      </div>

      {lightbox && (
        <div className="ue-lightbox" onClick={() => setLightbox(null)} data-testid="ue-lightbox">
          <button type="button" className="ue-lightbox-close" onClick={() => setLightbox(null)} aria-label="Close enlarged poster" data-testid="ue-lightbox-close">
            <X size={28} />
          </button>
          <img
            src={lightbox.poster}
            alt={`${lightbox.title} poster enlarged`}
            className="ue-lightbox-img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
};

export default UpcomingEvents;
